import React, { useState, useEffect, useRef } from "react";
import { Box, Grid, TextField, Tooltip, Typography } from "@mui/material";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { parse, format, isValid } from "date-fns";
import Section from "../../../layout/Section";
import ToggleChip from "../../../common/inputs/ToggleChip";
import { borderRadius } from "../../../../styles/theme";
import { colors } from "../../../../styles/colors";

const parseDateString = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const parsed = parse(dateStr.slice(0, 10), "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : null;
};

// Statische Beschriftung über dem Feld (wie bei Notion-Properties) statt
// MUI-Floating-Label, damit das Label auch bei leerem Wert klein & stabil bleibt.
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ display: "block", color: colors.text.secondary, mb: 0.5 }}
  >
    {children}
  </Typography>
);

// Rahmen ist im Ruhezustand unsichtbar und erscheint erst bei Hover/Focus,
// statt wie im MUI-Standard permanent sichtbar zu sein. Bei readOnly bleibt
// er dauerhaft transparent (keine Editier-Affordanz nötig).
const getFieldSx = (canEdit: boolean) => ({
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },
  ...(canEdit && {
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.border.default,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.border.focus,
      borderWidth: 2,
    },
  }),
});

// Datumsfelder erhalten global (theme.ts) einen abweichenden Pillen-Radius
// und permanenten Schatten für MuiPickersOutlinedInput; hier lokal auf den
// Standard-Radius zurückgesetzt und ebenfalls auf Hover/Focus-only-Rahmen umgestellt.
const getDateFieldSx = (canEdit: boolean) => ({
  "& .MuiPickersOutlinedInput-root": {
    borderRadius: `${borderRadius.md}px`,
    boxShadow: "none",
    "&:hover": { boxShadow: "none" },
  },
  "& .MuiPickersOutlinedInput-notchedOutline": {
    borderRadius: `${borderRadius.md}px`,
    borderColor: "transparent",
  },
  ...(canEdit && {
    "& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline":
      {
        borderColor: colors.border.default,
      },
    "& .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline":
      {
        borderColor: colors.border.focus,
        borderWidth: 2,
      },
  }),
});

interface MasterDataSectionProps {
  projektname: string;
  baubeginn: string;
  inbetriebnahme: string;
  laufzeitJahre: number;
  canEdit: boolean;
  fieldErrors?: Record<string, string>;
  onFieldChange: (
    field: string,
    value: string | number | boolean | null,
  ) => void;
}

const parseFormattedNumber = (value: string): string =>
  value.replace(/\./g, "").replace(",", ".");

interface NumericFormatAdapterProps {
  inputRef: (instance: HTMLInputElement | null) => void;
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  allowDecimals?: boolean;
  // Einheit direkt hinter dem Wert (z.B. " Jahre"), statt als abgesetztes
  // Adornment am rechten Feldrand — hält Wert und Einheit visuell zusammen.
  suffix?: string;
}

// Adapter für react-number-format als MUI-TextField-inputComponent (Deutsches Zahlenformat).
const NumericFormatAdapter = React.forwardRef<
  NumericFormatProps,
  NumericFormatAdapterProps
>(function NumericFormatAdapter(props, _ref) {
  const { inputRef, onChange, allowDecimals = true, suffix, ...other } = props;
  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      thousandSeparator="."
      decimalSeparator=","
      allowNegative={false}
      decimalScale={allowDecimals ? undefined : 0}
      fixedDecimalScale={false}
      suffix={suffix}
      onValueChange={(values) => {
        onChange({ target: { name: props.name, value: values.value } });
      }}
    />
  );
});

/**
 * Stammdaten-Section des Projektrahmens.
 * Enthält die grundlegenden Projektparameter.
 */
const MasterDataSection: React.FC<MasterDataSectionProps> = ({
  projektname,
  baubeginn,
  inbetriebnahme,
  laufzeitJahre,
  canEdit,
  fieldErrors = {},
  onFieldChange,
}) => {
  const [localName, setLocalName] = useState(projektname);
  const [localLaufzeit, setLocalLaufzeit] = useState(String(laufzeitJahre));

  const laufzeitRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setLocalName(projektname);
  }, [projektname]);

  useEffect(() => {
    setLocalLaufzeit(String(laufzeitJahre));
  }, [laufzeitJahre]);

  const handleLaufzeitBlur = () => {
    const raw = laufzeitRef.current?.value ?? localLaufzeit;
    const num = parseInt(parseFormattedNumber(raw.split(" ")[0]), 10);
    if (!isNaN(num) && num >= 1 && num <= 50) {
      onFieldChange("laufzeit_jahre", num);
    }
  };

  const handleDateChange = (field: string, newValue: Date | null) => {
    if (newValue && isValid(newValue)) {
      onFieldChange(field, format(newValue, "yyyy-MM-dd"));
    } else {
      onFieldChange(field, null);
    }
  };

  return (
    <Section title="Stammdaten">
      <Grid container spacing={3} sx={{ py: 1 }}>
        {/* Projektname */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box>
            <FieldLabel>Projektname</FieldLabel>
            <TextField
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              onBlur={() => {
                if (localName.trim()) {
                  onFieldChange("name", localName.trim());
                }
              }}
              size="small"
              fullWidth
              sx={getFieldSx(canEdit)}
              slotProps={{ input: { readOnly: !canEdit } }}
            />
          </Box>
        </Grid>

        {/* Betriebslaufzeit */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box>
            <FieldLabel>Betriebslaufzeit</FieldLabel>
            <TextField
              value={localLaufzeit}
              onChange={(e) => setLocalLaufzeit(e.target.value)}
              onBlur={handleLaufzeitBlur}
              size="small"
              fullWidth
              sx={getFieldSx(canEdit)}
              inputRef={laufzeitRef}
              slotProps={{
                input: {
                  readOnly: !canEdit,
                  inputComponent: NumericFormatAdapter as any,
                },
                htmlInput: { allowDecimals: false, suffix: " Jahre" },
              }}
            />
          </Box>
        </Grid>

        {/* Baubeginn */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box>
            <FieldLabel>Baubeginn</FieldLabel>
            <DatePicker
              value={parseDateString(baubeginn)}
              onChange={(newValue) => handleDateChange("baubeginn", newValue)}
              disabled={!canEdit}
              format="dd.MM.yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  error: !!fieldErrors.baubeginn,
                  helperText: fieldErrors.baubeginn,
                  sx: getDateFieldSx(canEdit),
                },
                openPickerButton: {
                  sx: canEdit
                    ? { "& .MuiSvgIcon-root": { fontSize: 20 } }
                    : { display: "none" },
                },
              }}
            />
          </Box>
        </Grid>

        {/* Inbetriebnahme */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box>
            <FieldLabel>Inbetriebnahme</FieldLabel>
            <DatePicker
              value={parseDateString(inbetriebnahme)}
              onChange={(newValue) =>
                handleDateChange("inbetriebnahme", newValue)
              }
              disabled={!canEdit}
              format="dd.MM.yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  error: !!fieldErrors.inbetriebnahme,
                  helperText: fieldErrors.inbetriebnahme,
                  sx: getDateFieldSx(canEdit),
                },
                openPickerButton: {
                  sx: canEdit
                    ? { "& .MuiSvgIcon-root": { fontSize: 20 } }
                    : { display: "none" },
                },
              }}
            />
          </Box>
        </Grid>

      </Grid>
    </Section>
  );
};

export default React.memo(MasterDataSection);
